// ============================================================================
// BREAK-GLASS GRANTS
// In-memory, time-limited, resource-scoped break-glass grants for System
// Administrators. The axios interceptors attach the X-Break-Glass-* headers to
// read/delete requests for the granted resource only, and report 403s that a
// break-glass grant could resolve. Nothing is persisted: a reload starts clean.
// ============================================================================

export interface BreakGlassContext {
  category: string;
  justification: string;
}

// Mirrors backend/src/utils/break-glass.ts BREAK_GLASS_CATEGORIES.
export const BREAK_GLASS_CATEGORIES: { value: string; label: string }[] = [
  { value: 'CUSTOMER_REQUEST', label: 'Customer / client request' },
  { value: 'COMPLIANCE_INVESTIGATION', label: 'Compliance / regulatory investigation' },
  { value: 'DATA_DELETION', label: 'Data deletion / GDPR request' },
  { value: 'SECURITY_INCIDENT', label: 'Security incident response' },
  { value: 'TECHNICAL_SUPPORT', label: 'Technical support / bug investigation' },
];

export const BREAK_GLASS_MIN_JUSTIFICATION = 10;
export const BREAK_GLASS_TTL_MS = 30 * 60 * 1000;

export function validateBreakGlass(ctx: Partial<BreakGlassContext>): string | null {
  if (!ctx.category || !BREAK_GLASS_CATEGORIES.some(c => c.value === ctx.category)) {
    return 'Select a reason category';
  }
  if ((ctx.justification || '').trim().length < BREAK_GLASS_MIN_JUSTIFICATION) {
    return `Justification must be at least ${BREAK_GLASS_MIN_JUSTIFICATION} characters`;
  }
  return null;
}

export function breakGlassHeaders(ctx?: BreakGlassContext | null): Record<string, string> {
  if (!ctx) return {};
  return {
    'X-Break-Glass-Category': ctx.category,
    'X-Break-Glass-Justification': ctx.justification,
  };
}

// 'list' scopes are named admin list endpoints (see LIST_ENDPOINTS).
export type BreakGlassScopeType = 'project' | 'session' | 'list';
export interface BreakGlassScope {
  type: BreakGlassScopeType;
  id: string;
}

export type BreakGlassListId = 'projects' | 'admin-sessions';

const LIST_ENDPOINTS: Record<BreakGlassListId, RegExp[]> = {
  projects: [/^(?:\/api)?\/projects\/?$/],
  'admin-sessions': [/^(?:\/api)?\/admin\/sessions\/active\/?$/, /^(?:\/api)?\/sessions\/?$/],
};

export interface BreakGlassGrant extends BreakGlassContext {
  scope: BreakGlassScope;
  grantedAt: number;
  expiresAt: number;
  // Route the grant belongs to. Navigating outside it ends the grant.
  path?: string;
  exactPath?: boolean;
}

export interface GrantOptions {
  ttlMs?: number;
  path?: string;
  exactPath?: boolean;
  now?: number;
}

export interface BreakGlassDenial {
  scope: BreakGlassScope;
  url: string;
  action?: string;
  // True when the refused request already carried break-glass headers.
  withBreakGlass: boolean;
}

const grants = new Map<string, BreakGlassGrant>();
// sessionId -> projectId, learnt from GET /projects/:id/sessions. A project
// grant covers that project's sessions too (Cedar allows Session::Read with
// break-glass for System Administrators), so opening one does not re-prompt.
const sessionProjects = new Map<string, string>();
const listeners = new Set<() => void>();
const denialListeners = new Set<(d: BreakGlassDenial) => void>();

export const scopeKey = (s: BreakGlassScope) => `${s.type}:${s.id}`;

const notify = () => listeners.forEach(l => l());

export function subscribeBreakGlass(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function subscribeBreakGlassDenied(fn: (d: BreakGlassDenial) => void): () => void {
  denialListeners.add(fn);
  return () => { denialListeners.delete(fn); };
}

export function grantBreakGlass(scope: BreakGlassScope, ctx: BreakGlassContext, opts: GrantOptions = {}): BreakGlassGrant {
  const error = validateBreakGlass(ctx);
  if (error) throw new Error(error);
  const now = opts.now ?? Date.now();
  const grant: BreakGlassGrant = {
    category: ctx.category,
    justification: ctx.justification.trim(),
    scope,
    grantedAt: now,
    expiresAt: now + (opts.ttlMs ?? BREAK_GLASS_TTL_MS),
    path: opts.path,
    exactPath: opts.exactPath,
  };
  grants.set(scopeKey(scope), grant);
  notify();
  return grant;
}

export function getActiveGrant(scope: BreakGlassScope, now = Date.now()): BreakGlassGrant | null {
  const g = grants.get(scopeKey(scope));
  return g && g.expiresAt > now ? g : null;
}

export function rememberSessionProjects(projectId: string, sessionIds: string[]): void {
  sessionIds.forEach(id => { if (id) sessionProjects.set(id, projectId); });
}

/** The active project grant covering a session, when the session's project is known. */
export function projectGrantForSession(sessionId: string, now = Date.now()): BreakGlassGrant | null {
  const projectId = sessionProjects.get(sessionId);
  return projectId ? getActiveGrant({ type: 'project', id: projectId }, now) : null;
}

/** The scope's own grant, or for a session the grant of its project. */
export function getEffectiveGrant(scope: BreakGlassScope, now = Date.now()): BreakGlassGrant | null {
  return getActiveGrant(scope, now) ?? (scope.type === 'session' ? projectGrantForSession(scope.id, now) : null);
}

export function endBreakGlass(scope: BreakGlassScope): void {
  if (grants.delete(scopeKey(scope))) notify();
}

export function clearAllBreakGlass(): void {
  sessionProjects.clear();
  if (grants.size === 0) return;
  grants.clear();
  notify();
}

export function pruneExpired(now = Date.now()): void {
  let changed = false;
  grants.forEach((g, k) => {
    if (g.expiresAt <= now) { grants.delete(k); changed = true; }
  });
  if (changed) notify();
}

const SESSION_PAGE = /^\/(?:session|second-screen)\/([^/]+)/;

const pathCovers = (g: BreakGlassGrant, pathname: string) => {
  if (!g.path) return true;
  if (g.scope.type === 'project') {
    const page = SESSION_PAGE.exec(pathname);
    if (page && sessionProjects.get(decodeURIComponent(page[1])) === g.scope.id) return true;
  }
  if (g.exactPath) return pathname === g.path;
  return pathname === g.path || pathname.startsWith(g.path.endsWith('/') ? g.path : `${g.path}/`);
};

export function pruneForPath(pathname: string): void {
  let changed = false;
  grants.forEach((g, k) => {
    if (!pathCovers(g, pathname)) { grants.delete(k); changed = true; }
  });
  if (changed) notify();
}

export function nextExpiry(): number | null {
  let min: number | null = null;
  grants.forEach(g => { if (min === null || g.expiresAt < min) min = g.expiresAt; });
  return min;
}

export function urlPath(url?: string): string {
  if (!url) return '';
  let path = url;
  const m = /^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i.exec(path);
  if (m) path = m[1] || '/';
  path = path.split(/[?#]/)[0];
  return path.startsWith('/') ? path : `/${path}`;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function scopeMatchesUrl(scope: BreakGlassScope, url?: string): boolean {
  const path = urlPath(url);
  if (scope.type === 'list') {
    const patterns = LIST_ENDPOINTS[scope.id as BreakGlassListId] || [];
    return patterns.some(p => p.test(path));
  }
  // Session-scoped reads also live outside /sessions/<id>: device images,
  // whiteboard sources and the /api/<id>/tasks mount.
  const prefixes = scope.type === 'project'
    ? ['/projects/']
    : ['/sessions/', '/device-images/', '/whiteboard/sources/', '/api/'];
  const ids = [escapeRe(encodeURIComponent(scope.id)), escapeRe(scope.id)];
  return prefixes.some(prefix => ids.some(id => {
    const tail = prefix === '/api/' ? '/tasks(?:/|$)' : '(?:/|$)';
    return new RegExp(`${escapeRe(prefix)}${id}${tail}`).test(path);
  }));
}

// Break-glass covers reads and deletes. Writes and ACL changes on other people's
// resources are never allowed, so their requests go out without the headers.
const BREAK_GLASS_METHODS = new Set(['get', 'head', 'delete']);

// POST /api/s3/presigned-url is a read: the backend checks Project::Read or
// Session::Read on the resource named by the key prefix (s3-presigned.ts).
const PRESIGN_PATH = /^(?:\/api)?\/s3\/presigned-url$/;
const SESSION_KEY_PREFIXES = ['sessions/', 'tts-audio/', 'device-audio/', 'device-images/', 'generated-images/'];

export function presignScope(data: unknown): BreakGlassScope | null {
  let body: any = data;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { return null; }
  }
  const key: unknown = body?.key;
  if (typeof key !== 'string') return null;
  const idAfter = (prefix: string) => key.slice(prefix.length).split('/')[0] || null;
  if (key.startsWith('projects/')) {
    const id = idAfter('projects/');
    return id ? { type: 'project', id } : null;
  }
  const prefix = SESSION_KEY_PREFIXES.find(p => key.startsWith(p));
  const id = prefix ? idAfter(prefix) : null;
  return id ? { type: 'session', id } : null;
}

export function headersForRequest(method: string | undefined, url: string | undefined, now = Date.now(), data?: unknown): Record<string, string> {
  const m = (method || 'get').toLowerCase();
  if (m === 'post' && PRESIGN_PATH.test(urlPath(url))) {
    const scope = presignScope(data);
    return breakGlassHeaders(scope ? getEffectiveGrant(scope, now) : null);
  }
  if (!BREAK_GLASS_METHODS.has(m)) return {};
  let found: BreakGlassGrant | null = null;
  grants.forEach(g => {
    if (!found && g.expiresAt > now && scopeMatchesUrl(g.scope, url)) found = g;
  });
  if (!found) {
    sessionProjects.forEach((projectId, sessionId) => {
      if (found) return;
      const g = getActiveGrant({ type: 'project', id: projectId }, now);
      if (g && scopeMatchesUrl({ type: 'session', id: sessionId }, url)) found = g;
    });
  }
  return breakGlassHeaders(found);
}

const READ_ACTIONS: Record<string, BreakGlassScopeType> = {
  'Project::Read': 'project',
  'Session::Read': 'session',
};

const PRIMARY_RESOURCE = /^(?:\/api)?\/(projects|sessions)\/([^/]+)\/?$/;

// Works out which resource a 403 response is about, if break-glass could open it.
export function denialScope(method: string | undefined, url: string | undefined, body: any): BreakGlassScope | null {
  const m = (method || 'get').toLowerCase();
  if (m !== 'get' && m !== 'head') return null;
  const path = urlPath(url);
  const action: string | undefined = body && typeof body === 'object' ? body.action : undefined;

  if (action === 'Project::ListAll' || action === 'Session::ListAll') {
    const list = (Object.keys(LIST_ENDPOINTS) as BreakGlassListId[]).find(id => scopeMatchesUrl({ type: 'list', id }, path));
    return list ? { type: 'list', id: list } : null;
  }
  if (action && READ_ACTIONS[action]) {
    const segment = READ_ACTIONS[action] === 'project' ? 'projects' : 'sessions';
    const r = new RegExp(`/${segment}/([^/]+)`).exec(path);
    return r ? { type: READ_ACTIONS[action], id: decodeURIComponent(r[1]) } : null;
  }
  const primary = PRIMARY_RESOURCE.exec(path);
  if (primary) {
    return { type: primary[1] === 'projects' ? 'project' : 'session', id: decodeURIComponent(primary[2]) };
  }
  for (const id of Object.keys(LIST_ENDPOINTS) as BreakGlassListId[]) {
    if (scopeMatchesUrl({ type: 'list', id }, path) && (id !== 'projects')) return { type: 'list', id };
  }
  return null;
}

interface InterceptableClient {
  interceptors: {
    request: { use: (onFulfilled: (config: any) => any) => number };
    response: { use: (onFulfilled: (r: any) => any, onRejected: (e: any) => any) => number };
  };
}

export function attachBreakGlassHeaders(config: any): any {
  const extra = headersForRequest(config?.method, config?.url, Date.now(), config?.data);
  if (Object.keys(extra).length === 0) return config;
  const headers = config.headers || {};
  const has = (name: string) =>
    typeof headers.has === 'function' ? headers.has(name) : Object.keys(headers).some(k => k.toLowerCase() === name.toLowerCase());
  for (const [k, v] of Object.entries(extra)) {
    if (has(k)) continue;
    if (typeof headers.set === 'function') headers.set(k, v);
    else headers[k] = v;
  }
  config.headers = headers;
  return config;
}

export function reportBreakGlassDenial(error: any): void {
  if (error?.response?.status !== 403) return;
  const cfg = error.config || {};
  const scope = denialScope(cfg.method, cfg.url, error.response.data);
  if (!scope) return;
  const h = cfg.headers || {};
  const withBreakGlass = typeof h.get === 'function'
    ? !!h.get('X-Break-Glass-Category')
    : Object.keys(h).some(k => k.toLowerCase() === 'x-break-glass-category');
  const denial: BreakGlassDenial = { scope, url: urlPath(cfg.url), action: error.response.data?.action, withBreakGlass };
  denialListeners.forEach(l => l(denial));
}

const PROJECT_SESSIONS = /^(?:\/api)?\/projects\/([^/]+)\/sessions\/?$/;

export function recordProjectSessions(response: any): void {
  const cfg = response?.config || {};
  if ((cfg.method || 'get').toLowerCase() !== 'get') return;
  const m = PROJECT_SESSIONS.exec(urlPath(cfg.url));
  const sessions = response?.data?.sessions;
  if (!m || !Array.isArray(sessions)) return;
  rememberSessionProjects(decodeURIComponent(m[1]), sessions.map((x: any) => x?.sessionId || x?.id).filter(Boolean));
}

export function installBreakGlassInterceptors(client: InterceptableClient): void {
  client.interceptors.request.use(attachBreakGlassHeaders);
  client.interceptors.response.use(
    (r) => { recordProjectSessions(r); return r; },
    (error) => {
      reportBreakGlassDenial(error);
      return Promise.reject(error);
    }
  );
}
